import { useEffect, useState } from 'react';

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

function WriteReviewModal({ open, onClose, onSubmit, submitting = false }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !submitting) onClose?.();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose, submitting]);

  useEffect(() => {
    if (!open) {
      setRating(0);
      setHoverRating(0);
      setComment('');
      setError('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (!rating) {
      setError('Please select a star rating');
      return;
    }

    setError('');
    await onSubmit?.({ rating, comment: comment.trim() });
  };

  const displayRating = hoverRating || rating;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="write-review-title"
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-[440px] rounded-3xl bg-white p-6 shadow-2xl sm:p-7"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="write-review-title" className="text-xl font-bold text-[#1B1B1B] sm:text-2xl">
            Write A Review
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#D9D9D9] text-[#666666] transition hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          <div>
            <p className="text-sm font-semibold text-[#1B1B1B]">
              Rating <span className="text-[#E85D5D]">*</span>
            </p>
            <div className="mt-2 flex items-center gap-1" onMouseLeave={() => setHoverRating(0)}>
              {[1, 2, 3, 4, 5].map((star) => {
                const active = star <= displayRating;
                return (
                  <button
                    key={star}
                    type="button"
                    disabled={submitting}
                    onMouseEnter={() => setHoverRating(star)}
                    onClick={() => {
                      setRating(star);
                      setError('');
                    }}
                    className="rounded p-0.5 transition hover:scale-110 disabled:opacity-50"
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                  >
                    <svg
                      className={`h-8 w-8 ${active ? 'text-amber-400' : 'text-[#D9D9D9]'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      aria-hidden
                    >
                      <path d={STAR_PATH} />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="review-comment" className="text-sm font-semibold text-[#1B1B1B]">
              Review <span className="font-medium text-[#999999]">(optional)</span>
            </label>
            <textarea
              id="review-comment"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Share your experience with this course..."
              rows={5}
              disabled={submitting}
              className="mt-2 w-full resize-none rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] px-4 py-3 text-sm leading-relaxed text-[#1B1B1B] outline-none transition placeholder:text-[#AAAAAA] focus:border-[#9369CC] focus:bg-white disabled:opacity-50"
            />
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center rounded-full bg-[#9369CC] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#7f55b8] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default WriteReviewModal;
